/**
 * Projection for `OemConnectorsScene` — the demo's connector grid
 * (`CONN` at demo line 2456, rendered at demo line 2479).
 *
 * ## The one interesting problem: the stat chips
 *
 * Each connector card carries two or three stat chips, and in the demo every
 * one of them was a template literal evaluated against the loaded data pack —
 * `` `${B.parts.length} parts` ``. The seed could not carry the number without
 * freezing it against one pack, so `shared/seed/connectors.ts` carries the
 * **expression verbatim** as a {@link LiveFigure} segment and evaluates
 * nothing. Resolving it is this file's job.
 *
 * {@link CONNECTOR_FIGURES} is the whole resolution table: one entry per
 * distinct expression that appears in the seed, each a pure function of an
 * explicit {@link ConnectorFigureCtx}. Nothing is `eval`'d, nothing reads a
 * global, and an expression with no entry resolves to `null` — which the scene
 * renders by dropping the chip rather than printing a dangling unit.
 *
 * The context is assembled from **eight lazy domains plus the lake index**.
 * That is the honest cost of a page whose entire point is "here is what every
 * connected system is currently feeding us"; the figures are the content. Every
 * one of those domains is shared with another surface through the module-level
 * promise cache in `useOemData`, so a visitor arriving from the fleet or cost
 * pages has most of them already.
 *
 * Pure: no React, no fetching, no clock. Transformation layer (layer 3).
 *
 * @see src/versions/v1/modules/integration/OemConnectorsScene.tsx
 * @see src/versions/v1/shared/seed/connectors.ts
 * @see docs/logic/integration/connectors.md
 */

import { FLEET } from '../../data/core';
import type { CommsPayload, DocIndexPayload, LakeIndex } from '../../schema/document';
import type { PartsPayload } from '../../schema/part';
import type { PeoplePayload } from '../../schema/person';
import type { RmsPayload } from '../../schema/telemetry';
import type { OilSample, TyreTable } from '../../schema/component';
import type { ServicePayload } from '../../schema/service';
import type { WorkOrder, WorkOrdersPayload } from '../../schema/work-order';
import { unpackTable } from '../../shared/columnar';
import { groupNum } from '../../shared/oem-engine';
import { CONNECTORS, type OemConnector } from '../../shared/seed/connectors';
import type { LiveFigure } from '../../shared/seed/modules';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * Everything the stat expressions read. Every field is optional: the scene
 * renders as each domain lands, and an unresolved expression drops its chip.
 */
export interface ConnectorFigureCtx {
  readonly workOrders?: WorkOrdersPayload;
  readonly service?: ServicePayload;
  readonly parts?: PartsPayload;
  readonly people?: PeoplePayload;
  readonly rms?: RmsPayload;
  readonly comms?: CommsPayload;
  readonly docIndex?: DocIndexPayload;
  readonly oil?: readonly OilSample[];
  readonly tyres?: TyreTable;
  readonly lake?: LakeIndex;
}

/** Cached unpack of the work-order table — the only columnar read here. */
const wos = (ctx: ConnectorFigureCtx): readonly WorkOrder[] | null =>
  ctx.workOrders ? unpackTable<WorkOrder>(ctx.workOrders.wos) : null;

// ---------------------------------------------------------------------------
// The expression table
// ---------------------------------------------------------------------------

/**
 * Demo lines 2456–2478. Every distinct stat expression in `CONNECTORS`, as a
 * pure function.
 *
 * Keys are the expression strings **exactly as the seed carries them**, so a
 * seed edit that changes an expression fails loudly (the chip disappears)
 * rather than silently printing a stale number.
 */
export const CONNECTOR_FIGURES: Readonly<
  Record<string, (ctx: ConnectorFigureCtx) => number | string | null>
> = {
  // ---- IBM Maximo · service register ------------------------------------
  // `B.mx` is the per-machine Maximo record map: one entry per machine in the
  // register, so the eager fleet count is the same figure without a fetch.
  'Object.keys(B.mx).length': () => FLEET.length,
  'B.mx_contracts.length': (c) => c.service?.mx_contracts.length ?? null,

  // ---- SAP PM / Maximo · Gawler Deep -------------------------------------
  "WOS.filter(w=>w.mine!=='GD').length.toLocaleString()": (c) => {
    const rows = wos(c);
    return rows ? groupNum(rows.filter((w) => w.mine !== 'GD').length) : null;
  },
  "WOS.filter(w=>w.mine==='GD').length.toLocaleString()": (c) => {
    const rows = wos(c);
    return rows ? groupNum(rows.filter((w) => w.mine === 'GD').length) : null;
  },
  'B.maximo_assets.length': (c) => c.service?.maximo_assets.length ?? null,

  // ---- Pitram / telematics ----------------------------------------------
  "FLEET.filter(f=>['KG','BR','MR'].includes(f.mine)).length": () =>
    FLEET.filter((f) => ['KG', 'BR', 'MR'].includes(f.mine)).length,
  "FLEET.filter(f=>['MI','GD'].includes(f.mine)&&f.cls!=='sdrill').length": () =>
    FLEET.filter((f) => ['MI', 'GD'].includes(f.mine) && f.cls !== 'sdrill').length,

  // ---- RMS ---------------------------------------------------------------
  'Object.keys(B.rms_health).length': (c) =>
    c.rms ? Object.keys(c.rms.rms_health).length : null,
  'B.rms_alerts.length': (c) => c.rms?.rms_alerts.length ?? null,

  // ---- Salesforce --------------------------------------------------------
  'B.parts.length': (c) => c.parts?.parts.length ?? null,
  'B.cases.filter(c=>/Open/.test(c.status)).length': (c) =>
    c.service ? c.service.cases.filter((x) => /Open/.test(x.status)).length : null,

  // ---- CatBase -----------------------------------------------------------
  "B.extra.filter(e=>e.folder==='14_catbase'&&e.kind==='pdf').length": (c) =>
    c.lake
      ? c.lake.entries.filter((e) => e.folder === '14_catbase' && e.kind === 'pdf').length
      : null,
  'B.catbase_sections.length': (c) => c.parts?.catbase_sections.length ?? null,

  // ---- SharePoint / bulletins -------------------------------------------
  'B.docs.length': (c) => c.docIndex?.docs.length ?? null,
  'B.bulletins.length': (c) => c.parts?.bulletins.length ?? null,

  // ---- Maximo Mobile -----------------------------------------------------
  'B.tapp_jobs.length': (c) => c.service?.tapp_jobs.length ?? null,
  'B.tapp_techs.length': (c) => c.people?.tapp_techs.length ?? null,

  // ---- XReach ------------------------------------------------------------
  'B.xreach_sessions.length': (c) => c.service?.xreach_sessions.length ?? null,
  'Math.round(B.xreach_sessions.filter(x=>x.first_time_fix).length/B.xreach_sessions.length*100)':
    (c) => {
      const s = c.service?.xreach_sessions;
      if (!s || s.length === 0) return null;
      return Math.round((s.filter((x) => x.first_time_fix).length / s.length) * 100);
    },

  // ---- Outlook / Teams ---------------------------------------------------
  'B.emails.length': (c) => c.comms?.emails.length ?? null,
  'B.meetings.length': (c) => c.comms?.meetings.length ?? null,
  'B.meetings.reduce((a,m)=>a+m.duration_min,0)': (c) =>
    c.comms ? c.comms.meetings.reduce((a, m) => a + m.duration_min, 0) : null,

  // ---- Oil analysis ------------------------------------------------------
  'B.oil.length': (c) => c.oil?.length ?? null,
  "B.oil.filter(o=>o.verdict!=='Normal').length": (c) =>
    c.oil ? c.oil.filter((o) => o.verdict !== 'Normal').length : null,

  // ---- Tyres -------------------------------------------------------------
  'B.tyres.rows.length': (c) => c.tyres?.rows.length ?? null,
  // Column 10 is `Action` — a non-empty cell means the inspection raised one.
  'B.tyres.rows.filter(r=>r[10]).length': (c) =>
    c.tyres ? c.tyres.rows.filter((r) => Boolean(r[10])).length : null,

  // ---- LMS ---------------------------------------------------------------
  'B.lms.length': (c) => c.service?.lms.length ?? null,
  "B.lms.filter(r=>r.status!=='Current').length": (c) =>
    c.service ? c.service.lms.filter((r) => r.status !== 'Current').length : null,

  // ---- Rostering / CSS ---------------------------------------------------
  'B.roster.rows.length': (c) => c.people?.roster.rows.length ?? null,
  'B.css.rows.length': (c) => c.people?.css.rows.length ?? null,
  // Column 10 is `Overall`, scored 1–5.
  '(B.css.rows.reduce((a,r)=>a+r[10],0)/B.css.rows.length).toFixed(1)': (c) => {
    const rows = c.people?.css.rows;
    if (!rows || rows.length === 0) return null;
    const total = rows.reduce((a: number, r) => a + Number(r[10] ?? 0), 0);
    return (total / rows.length).toFixed(1);
  },
};

// ---------------------------------------------------------------------------
// Rendering a figure
// ---------------------------------------------------------------------------

/** One resolved chip: the text, and whether it opens with a computed number. */
export interface ResolvedStat {
  /** Stable key within a card — the seed's segment shape is stable. */
  readonly key: string;
  /** Everything before the first text segment, when the chip opens with a figure. */
  readonly lead: string | null;
  /** The rest of the chip, joined. */
  readonly text: string;
}

/**
 * Resolve one {@link LiveFigure} against the context.
 *
 * Returns `null` when any `expr` segment has no entry in
 * {@link CONNECTOR_FIGURES} or has not resolved yet — the scene then drops the
 * chip, because `' WOs'` with no number reads as a bug.
 *
 * `lead` reproduces the demo's `s.replace(/^([\d,]+)/,'<b>$1</b>')`: a chip
 * that opens with a number had that number bolded. Here the structure says so
 * directly — a chip whose first segment is an `expr` is exactly that case.
 */
export function resolveStat(figure: LiveFigure, ctx: ConnectorFigureCtx, index: number): ResolvedStat | null {
  const parts: string[] = [];
  let lead: string | null = null;

  for (let i = 0; i < figure.length; i++) {
    const segment = figure[i];
    if ('text' in segment) {
      parts.push(segment.text);
      continue;
    }
    const resolver = CONNECTOR_FIGURES[segment.expr];
    const value = resolver ? resolver(ctx) : null;
    if (value === null || value === undefined) return null;
    const printed = typeof value === 'number' ? groupNum(value) : value;
    if (i === 0) lead = printed;
    else parts.push(printed);
  }

  return { key: `stat-${index}`, lead, text: parts.join('') };
}

/** Every chip on a card that resolves, in seed order. */
export const resolveStats = (
  connector: OemConnector,
  ctx: ConnectorFigureCtx,
): ResolvedStat[] =>
  connector.stats
    .map((figure, i) => resolveStat(figure, ctx, i))
    .filter((s): s is ResolvedStat => s !== null);

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

/** One connector card, with its chips already resolved. */
export interface ConnectorCard {
  readonly connector: OemConnector;
  readonly stats: readonly ResolvedStat[];
  /** `/integration/data?conn=<name>` — the connector's folder in the browser. */
  readonly to: string;
}

/** Demo line 2479. The 21 cards, in seed order. */
export const connectorCards = (ctx: ConnectorFigureCtx): ConnectorCard[] =>
  CONNECTORS.map((connector) => ({
    connector,
    stats: resolveStats(connector, ctx),
    to: `/integration/data?conn=${encodeURIComponent(connector.name)}`,
  }));

/** Demo line 2478 — `'<n> connectors · <n> live'`, the header chip. */
export const connectorCountChip = (): string =>
  `${CONNECTORS.length} connectors · ${CONNECTORS.filter((c) => c.status === 'Live').length} live`;
